// src/components/DebugPanel.tsx
'use client'

import React, { useState, useEffect } from 'react'
import { Monitor, X, Shield, Ban } from 'lucide-react'
import type { ConsentState, CategoryDefinition } from '../types'
import { getBlockedScriptsDebugInfo } from '../core/script-blocker'

interface DebugInfo {
  isReady: boolean
  hasConsent: boolean
  showBanner: boolean
  showSettings: boolean
  mode: string
  consent: ConsentState
  categories: CategoryDefinition[]
  position: string
  preset: string
  zIndex: number
}

interface DebugPanelProps {
  debugInfo: DebugInfo
}

function isProduction(): boolean {
  if (typeof window === 'undefined') return true

  if (typeof globalThis !== 'undefined') {
    const globalProcess = (globalThis as { process?: { env?: { NODE_ENV?: string } } }).process
    if (globalProcess?.env?.NODE_ENV) {
      return globalProcess.env.NODE_ENV === 'production'
    }
  }

  const port = window.location.port
  const hostname = window.location.hostname
  const devPorts = ['3000', '3001', '5173', '8080', '4200', '8000', '9000']
  const devHosts = ['localhost', '127.0.0.1', '0.0.0.0']
  const isDev = devHosts.includes(hostname) || devPorts.includes(port)

  return !isDev
}

export function DebugPanel({ debugInfo }: DebugPanelProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [position, setPosition] = useState<'bottom-right' | 'top-right'>('bottom-right')
  const [scriptInfo, setScriptInfo] = useState(getBlockedScriptsDebugInfo())

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+Shift+C to toggle cookie debug panel
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'C') {
        e.preventDefault()
        setIsOpen(prev => !prev)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Update script info periodically when panel is open
  useEffect(() => {
    if (!isOpen) return

    const interval = setInterval(() => {
      setScriptInfo(getBlockedScriptsDebugInfo())
    }, 1000)

    return () => clearInterval(interval)
  }, [isOpen])

  if (isProduction()) {
    return null
  }

  const triggerStyles: React.CSSProperties = {
    position: 'fixed',
    bottom: position === 'bottom-right' ? '80px' : 'auto',
    top: position === 'top-right' ? '20px' : 'auto',
    right: '20px',
    zIndex: 999999,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  }

  const buttonStyles: React.CSSProperties = {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    background: isOpen
      ? 'linear-gradient(135deg, #10b981, #059669)'
      : 'linear-gradient(135deg, #f9fafb, #f3f4f6)',
    border: '1px solid rgba(0, 0, 0, 0.1)',
    boxShadow: isOpen
      ? '0 10px 15px -3px rgba(16, 185, 129, 0.4), 0 4px 6px -2px rgba(16, 185, 129, 0.2)'
      : '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.04)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    color: isOpen ? '#ffffff' : '#6b7280',
  }

  const panelStyles: React.CSSProperties = {
    position: 'fixed',
    bottom: position === 'bottom-right' ? '140px' : 'auto',
    top: position === 'top-right' ? '80px' : 'auto',
    right: '20px',
    width: '360px',
    maxHeight: '600px',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    border: '1px solid #e5e7eb',
    overflow: 'hidden',
    zIndex: 999998,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  }

  return (
    <>
      {/* Trigger Button */}
      <div style={triggerStyles}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          style={buttonStyles}
          title="Cookie Debug (Ctrl+Shift+C)"
        >
          <Monitor size={20} />
        </button>
      </div>

      {/* Debug Panel */}
      {isOpen && (
        <div style={panelStyles}>
          {/* Header */}
          <div
            style={{
              padding: '16px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: '#f9fafb',
            }}
          >
            <div>
              <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#111827' }}>
                Cookie Debug Panel
              </h3>
              <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#6b7280' }}>
                Ctrl+Shift+C to toggle
              </p>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() =>
                  setPosition(position === 'bottom-right' ? 'top-right' : 'bottom-right')
                }
                style={{
                  padding: '6px',
                  fontSize: '10px',
                  color: '#6b7280',
                  backgroundColor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  cursor: 'pointer',
                }}
              >
                {position === 'bottom-right' ? '↑' : '↓'}
              </button>
              <button
                onClick={() => setIsOpen(false)}
                style={{
                  padding: '6px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#6b7280',
                }}
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Content */}
          <div style={{ maxHeight: '520px', overflowY: 'auto', padding: '16px' }}>
            {/* State */}
            <div style={{ marginBottom: '16px' }}>
              <h4
                style={{ margin: '0 0 8px', fontSize: '12px', fontWeight: 600, color: '#374151' }}
              >
                State
              </h4>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '8px',
                  fontSize: '11px',
                }}
              >
                <StateIndicator label="isReady" value={debugInfo.isReady} />
                <StateIndicator label="hasConsent" value={debugInfo.hasConsent} />
                <StateIndicator label="showBanner" value={debugInfo.showBanner} />
                <StateIndicator label="showSettings" value={debugInfo.showSettings} />
              </div>
            </div>

            {/* Config */}
            <div style={{ marginBottom: '16px' }}>
              <h4
                style={{ margin: '0 0 8px', fontSize: '12px', fontWeight: 600, color: '#374151' }}
              >
                Config
              </h4>
              <div style={{ fontSize: '11px', color: '#6b7280' }}>
                <div style={{ marginBottom: '4px' }}>
                  <span style={{ fontWeight: 500, color: '#374151' }}>Mode:</span> {debugInfo.mode}
                </div>
                <div style={{ marginBottom: '4px' }}>
                  <span style={{ fontWeight: 500, color: '#374151' }}>Preset:</span>{' '}
                  {debugInfo.preset}
                </div>
                <div style={{ marginBottom: '4px' }}>
                  <span style={{ fontWeight: 500, color: '#374151' }}>Position:</span>{' '}
                  {debugInfo.position}
                </div>
                <div>
                  <span style={{ fontWeight: 500, color: '#374151' }}>Z-Index:</span>{' '}
                  {debugInfo.zIndex}
                </div>
              </div>
            </div>

            {/* Consent */}
            <div style={{ marginBottom: '16px' }}>
              <h4
                style={{ margin: '0 0 8px', fontSize: '12px', fontWeight: 600, color: '#374151' }}
              >
                Consent ({Object.keys(debugInfo.consent).length} categories)
              </h4>
              <div style={{ fontSize: '11px' }}>
                {Object.entries(debugInfo.consent).map(([key, value]) => (
                  <div
                    key={key}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '6px 8px',
                      marginBottom: '4px',
                      backgroundColor: value ? '#dcfce7' : '#fee2e2',
                      borderRadius: '6px',
                    }}
                  >
                    <span style={{ color: '#374151' }}>{key}</span>
                    <span style={{ fontWeight: 600, color: value ? '#059669' : '#dc2626' }}>
                      {value ? 'Accepted' : 'Rejected'}
                    </span>
                  </div>
                ))}
                {Object.keys(debugInfo.consent).length === 0 && (
                  <div
                    style={{
                      padding: '8px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '6px',
                      color: '#6b7280',
                    }}
                  >
                    No consent data yet
                  </div>
                )}
              </div>
            </div>

            {/* Script Blocking */}
            <div style={{ marginBottom: '16px' }}>
              <h4
                style={{
                  margin: '0 0 8px',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#374151',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <Shield size={14} />
                Script Blocking
              </h4>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '8px',
                  marginBottom: '12px',
                }}
              >
                <div
                  style={{
                    padding: '8px',
                    backgroundColor: '#fee2e2',
                    borderRadius: '6px',
                    textAlign: 'center',
                  }}
                >
                  <div style={{ fontSize: '18px', fontWeight: 700, color: '#dc2626' }}>
                    {scriptInfo.totalBlocked}
                  </div>
                  <div style={{ fontSize: '10px', color: '#991b1b', marginTop: '2px' }}>
                    Blocked
                  </div>
                </div>
                <div
                  style={{
                    padding: '8px',
                    backgroundColor: '#dcfce7',
                    borderRadius: '6px',
                    textAlign: 'center',
                  }}
                >
                  <div style={{ fontSize: '18px', fontWeight: 700, color: '#059669' }}>
                    {scriptInfo.totalUnblocked}
                  </div>
                  <div style={{ fontSize: '10px', color: '#065f46', marginTop: '2px' }}>
                    Unblocked
                  </div>
                </div>
              </div>

              {/* Blocked Scripts */}
              {scriptInfo.blockedScripts.length > 0 && (
                <div style={{ marginBottom: '8px' }}>
                  <div
                    style={{
                      fontSize: '10px',
                      fontWeight: 600,
                      color: '#6b7280',
                      marginBottom: '4px',
                      textTransform: 'uppercase',
                    }}
                  >
                    Blocked Scripts
                  </div>
                  {scriptInfo.blockedScripts.map((script, idx) => (
                    <div
                      key={idx}
                      style={{
                        padding: '6px 8px',
                        marginBottom: '4px',
                        backgroundColor: '#fef2f2',
                        borderRadius: '4px',
                        fontSize: '10px',
                        borderLeft: '2px solid #dc2626',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          marginBottom: '2px',
                        }}
                      >
                        <Ban size={10} color="#dc2626" />
                        <span
                          style={{
                            fontWeight: 600,
                            color: '#dc2626',
                            fontSize: '9px',
                            textTransform: 'uppercase',
                          }}
                        >
                          {script.category}
                        </span>
                      </div>
                      <div
                        style={{
                          color: '#991b1b',
                          wordBreak: 'break-all',
                          fontSize: '9px',
                        }}
                      >
                        {script.src}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Unblocked Scripts */}
              {scriptInfo.unblockedScripts.length > 0 && (
                <div>
                  <div
                    style={{
                      fontSize: '10px',
                      fontWeight: 600,
                      color: '#6b7280',
                      marginBottom: '4px',
                      textTransform: 'uppercase',
                    }}
                  >
                    Unblocked Scripts
                  </div>
                  {scriptInfo.unblockedScripts
                    .slice(-5)
                    .reverse()
                    .map((script, idx) => (
                      <div
                        key={idx}
                        style={{
                          padding: '6px 8px',
                          marginBottom: '4px',
                          backgroundColor: '#f0fdf4',
                          borderRadius: '4px',
                          fontSize: '10px',
                          borderLeft: '2px solid #059669',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            marginBottom: '2px',
                          }}
                        >
                          <Shield size={10} color="#059669" />
                          <span
                            style={{
                              fontWeight: 600,
                              color: '#059669',
                              fontSize: '9px',
                              textTransform: 'uppercase',
                            }}
                          >
                            {script.category}
                          </span>
                        </div>
                        <div
                          style={{
                            color: '#065f46',
                            wordBreak: 'break-all',
                            fontSize: '9px',
                          }}
                        >
                          {script.src}
                        </div>
                      </div>
                    ))}
                </div>
              )}

              {scriptInfo.totalBlocked === 0 && scriptInfo.totalUnblocked === 0 && (
                <div
                  style={{
                    padding: '12px',
                    backgroundColor: '#f9fafb',
                    borderRadius: '6px',
                    color: '#6b7280',
                    fontSize: '11px',
                    textAlign: 'center',
                  }}
                >
                  No scripts detected. Add scripts with <code>data-cookie-category</code> attribute.
                </div>
              )}
            </div>

            {/* Categories */}
            <div>
              <h4
                style={{ margin: '0 0 8px', fontSize: '12px', fontWeight: 600, color: '#374151' }}
              >
                Categories ({debugInfo.categories.length})
              </h4>
              <div style={{ fontSize: '11px' }}>
                {debugInfo.categories.map(cat => (
                  <div
                    key={cat.id}
                    style={{
                      padding: '8px',
                      marginBottom: '6px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '6px',
                      borderLeft: cat.required ? '3px solid #3b82f6' : '3px solid #e5e7eb',
                    }}
                  >
                    <div style={{ fontWeight: 600, color: '#374151', marginBottom: '2px' }}>
                      {cat.label}
                      {cat.required && (
                        <span
                          style={{
                            marginLeft: '6px',
                            fontSize: '10px',
                            color: '#3b82f6',
                            fontWeight: 500,
                          }}
                        >
                          [Required]
                        </span>
                      )}
                    </div>
                    <div style={{ color: '#6b7280' }}>{cat.id}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

function StateIndicator({ label, value }: { label: string; value: boolean }) {
  return (
    <div
      style={{
        padding: '8px',
        backgroundColor: value ? '#dcfce7' : '#f3f4f6',
        borderRadius: '6px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <span style={{ color: '#374151', fontWeight: 500 }}>{label}</span>
      <span
        style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: value ? '#10b981' : '#9ca3af',
        }}
      />
    </div>
  )
}
